"use client";

import React from "react";
import { Car, Gauge, Settings, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 6,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function FahrzeugeGrid() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="149782" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden bg-card/40 backdrop-blur-md border-border/50">
            <Skeleton className="h-64 w-full rounded-none" />
            <CardHeader>
              <Skeleton className="h-8 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-10 w-full" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16 px-4 border border-destructive/20 rounded-xl bg-destructive/5">
        <p className="text-destructive font-medium">
          Unsere Leistungsübersicht konnte nicht geladen werden. Bitte versuchen Sie es später erneut.
        </p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-16 px-4 border border-border/50 rounded-xl bg-card/30 backdrop-blur-sm">
        <Car className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
        <p className="text-muted-foreground font-medium">
          Derzeit sind keine speziellen Leistungen in dieser Kategorie verfügbar.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="group flex flex-col overflow-hidden bg-card/80 backdrop-blur-md border-border/60 shadow-lg hover:shadow-primary/10 transition-all duration-500 rounded-2xl">
          <Link to={product.link} className="block relative overflow-hidden">
            <AspectRatio ratio={16 / 10}>
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Car className="w-16 h-16 text-muted-foreground/30" />
                </div>}
            </AspectRatio>
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60" />
            
            {product.categories && product.categories.length > 0 && <div className="absolute top-4 left-4 flex gap-2 flex-wrap z-10">
                {product.categories.slice(0, 2).map(cat => <Badge key={cat.id} variant="secondary" className="bg-primary/90 backdrop-blur-sm text-primary-foreground border-none shadow-sm font-sans">
                    {cat.name}
                  </Badge>)}
              </div>}
          </Link>

          <CardHeader className="pb-4 relative z-10">
            <div className="flex justify-between items-start gap-4">
              <Link to={product.link} className="hover:text-primary transition-colors flex-1">
                <CardTitle className="font-sans text-2xl font-bold tracking-tight text-foreground">
                  {product.title}
                </CardTitle>
              </Link>
              {product.price !== undefined && <div className="text-right shrink-0">
                  <span className="block text-xl font-bold text-primary">
                    ab ${product.price}
                  </span>
                  <span className="text-xs text-muted-foreground uppercase tracking-widest font-sans">
                    Startpreis
                  </span>
                </div>}
            </div>
          </CardHeader>

          <CardContent className="flex-grow pb-6">
            <div className="grid grid-cols-2 gap-y-4 gap-x-4 text-sm text-muted-foreground mb-6 font-sans">
              <div className="flex items-center gap-2.5">
                <Settings className="w-4 h-4 text-primary" />
                <span>Präzision</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Users className="w-4 h-4 text-primary" />
                <span>Profi-Team</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Gauge className="w-4 h-4 text-primary" />
                <span>Effizient</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Car className="w-4 h-4 text-primary" />
                <span>Premium</span>
              </div>
            </div>

            {product.short_description && <div className="text-sm text-muted-foreground/90 line-clamp-2 leading-relaxed font-sans [&_p]:mb-0" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
          </CardContent>

          <CardFooter className="pt-0 mt-auto">
            <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg rounded-full font-bold uppercase tracking-wider text-xs py-6" text="Angebot anfordern" showQuantity={false} />
          </CardFooter>
        </Card>)}
    </div>;
}
export default function Fahrzeuge() {
  return;
}